import type { ReactNode } from "react"
import { memo, useMemo, useRef, useState } from "react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/browser"
import { Avatar, Popover } from "@productlane/ui"
import {
	Command,
	CommandAddNewItem,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { PartialContactCreate } from "./create-contact-dialog"
import { analytics } from "@/lib/analytics"
import { useContactOptions } from "@/lib/use-entity-options"
import { CreateContactDialog } from "./create-contact-dialog"

interface CompanyContactSelectProps {
	onSelect: (contact: PartialContactCreate) => void
	trigger: ReactNode
	contactIds: Array<string>
}

export const CompanyContactSelect = memo(
	({ onSelect, trigger, contactIds }: CompanyContactSelectProps) => {
		const [open, setOpen] = useState(false)

		const [query, setQuery] = useState("")
		const { contacts } = useContactOptions({
			query,
			selectedIds: contactIds,
			enabled: open,
		})
		const filteredContacts = useMemo(() => {
			return contacts.filter(
				(c) =>
					c.name?.toLowerCase().includes(query) ||
					c.email.toLowerCase().includes(query),
			)
		}, [contacts, query])
		const parentRef = useRef<HTMLDivElement>(null)
		const [contactCreateValue, setContactCreateValue] = useState("")

		return (
			<>
				<CreateContactDialog
					defaultValue={contactCreateValue}
					isOpen={!!contactCreateValue}
					onOpenChange={(dialogOpen, newContact) => {
						if (!dialogOpen) {
							if (newContact) {
								setQuery("")
								onSelect(newContact)
								analytics.track("created-inline-contact")
							}
							setContactCreateValue("")
							analytics.track("close-inline-create-contact-dialog")
						}
					}}
				/>
				<Popover
					className="w-72 p-0"
					open={open}
					onOpenChange={setOpen}
					side="left"
					content={
						<Command shouldFilter={false}>
							<CommandInput
								placeholder="Search contacts..."
								autoFocus
								value={query}
								onValueChange={setQuery}
							/>
							<CommandList ref={parentRef} className="max-h-none">
								{!filteredContacts.length ? (
									<CommandAddNewItem
										onCreate={(value) => {
											setContactCreateValue(value)
										}}
										show={true}
										search={query}
										onAnalyticsTrack={() => {
											analytics.track("open-inline-create-contact-dialog")
										}}
									/>
								) : (
									<CommandGroup>
										<div className="h-64">
											<FixedSizeList
												height={256}
												width="100%"
												itemCount={filteredContacts.length}
												itemData={filteredContacts}
												itemKey={(index, data) => data[index]?.id ?? index}
												itemSize={36}
												overscanCount={5}
												className="!h-full"
											>
												{({ index, style }) => {
													const contact = filteredContacts[index]

													if (!contact) return null

													return (
														<CommandItem
															value={contact.id}
															onSelect={() => {
																setOpen(false)
																onSelect(contact)
																setQuery("")
															}}
															style={style}
															key={contact.id}
														>
															<div className="flex items-center gap-2">
																<Avatar
																	name={contact.name || contact.email}
																	image={contact.imageUrl}
																	size="xs"
																/>
																<div className="flex-1 truncate">
																	{contact.email}
																</div>

																<Check
																	className={cn(
																		"h-4 w-4",
																		contactIds.includes(contact.id)
																			? "opacity-100"
																			: "opacity-0",
																	)}
																/>
															</div>
														</CommandItem>
													)
												}}
											</FixedSizeList>
										</div>
									</CommandGroup>
								)}
							</CommandList>
						</Command>
					}
				>
					{trigger}
				</Popover>
			</>
		)
	},
)
